"use client";

import React from "react";
import Image from "next/image";
import { DrupalNode } from "next-drupal";
import { config } from "@/lib/config";
import Slider from "react-slick";

interface VerticalCardsProps {
  data: DrupalNode;
}

const CardItem = ({
  item,
  isTwoCards,
  isSlider,
}: {
  item: any;
  isTwoCards: boolean;
  isSlider?: boolean;
}) => (
  <div
    className={`flex flex-col items-center justify-between p-[40px] betweenMobileTab:p-[30px] mobileMax:p-[24px] minMobile:p-[20px] hover:scale-105 transition-all ease-in-out duration-700 bg-white rounded-[30px] ${isTwoCards ? "w-[430px] mobileMax:w-full" : "w-full"
      } ${isSlider ? "" : "h-full"} shadow-lg`}
  >
    <div className="flex flex-col items-center justify-center gap-6 mobileMax:gap-4 w-full">
      {item?.field_image?.uri?.url && (
        <div className="w-[250px] h-[250px] mobileMax:w-[200px] mobileMax:h-[200px] minMobile:w-[170px] minMobile:h-[170px] rounded-full overflow-hidden shrink-0">
          <Image
            alt={item.name}
            src={`${config.apiBase}${item?.field_image?.uri?.url || ""}`}
            width={250}
            height={250}
            priority
            quality={90}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="flex flex-col items-center justify-center gap-4 w-full">
        <div
          className="text-[14px] text-[#003350] font-poppins font-medium text-center 
           mobileMax:text-xsmall leading-normal mb-[15px] mobileMax:mb-2 designation"
          dangerouslySetInnerHTML={{
            __html: item?.field_description?.processed,
          }}
        />
      </div>
    </div>
  </div>
);

export default function VerticalCards({ data }: VerticalCardsProps) {
  const isBlue = data.field_background_color === "blue";
  const isWhite = data.field_background_color === "white";
  const isGray = data.field_background_color === "gray";
  const isDarkDray = data.field_background_color === "dark_gray";

  const bgClass = isBlue
    ? "bg-[#003350]"
    : isWhite
      ? "bg-white"
      : isGray
        ? "bg-[#f9f9f9]"
        : isDarkDray
          ? "bg-mapGray"
          : "bg-white";

  const cardsCount = data?.field_add_card?.length || 0;
  const isSlider = cardsCount > 3;

  const sliderSettings = {
    dots: true,
    arrows: false,
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 3,
    autoplay: true,
    autoplaySpeed: 4000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <div
      className={`${bgClass} py-[120px] relative pt-[80px] pb-[120px] mobileMax:pt-[50px] mobileMax:pb-[70px] betweenMobileTab:py-16 overflow-hidden`}
    >
      <div className="mini-container relative">
        <h3
          className={`text-[32px] ${data?.field_title_position === "left" ? "text-left" : "text-center"
            } font-poppins mb-[80px] mobileMax:mb-8 font-semibold leading-normal ${isBlue
              ? "bg-[#48DBB2]"
              : "bg-[linear-gradient(to_top,#8EBCE5,#11375C)]"
            } bg-clip-text text-transparent text-clip mobileMax:text-[28px] mobileToDesk:mb-5`}
        >
          {data?.field_title}
        </h3>

        {isSlider ? (
          <div className="vertical-cards-slider">
            <Slider {...sliderSettings}>
              {data?.field_add_card?.map((item: any) => (
                <div key={item.id} className="px-[25px] minMobile:px-[10px] pb-10 mobileMax:pb-6 pt-6 h-full flex">
                  <CardItem item={item} isTwoCards={false} />
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <div
            className={`${cardsCount <= 2
                ? "flex flex-wrap justify-center items-stretch"
                : "grid grid-cols-3 betweenMobileTab:grid-cols-2 mobileMax:grid-cols-1 items-stretch justify-center"
              } gap-[50px] betweenMobileTab:gap-[35px] mobileMax:gap-[20px]`}
          >
            {data?.field_add_card?.map((item: any) => (
              <CardItem
                key={item.id}
                item={item}
                isTwoCards={cardsCount <= 2}
                isSlider
              />
            ))}
          </div>
        )}
      </div>
      <style jsx global>{`
        .vertical-cards-slider .slick-track {
          display: flex !important;
        }
        .vertical-cards-slider .slick-slide {
          height: auto !important;
          display: flex !important;
        }
        .vertical-cards-slider .slick-slide > div {
          display: flex;
          flex: 1;
        }
        .vertical-cards-slider .slick-dots {
          bottom: -60px;
          display: flex !important;
          justify-content: center;
          align-items: center;
          gap: 12px;
        }
        @media (max-width: 1200px) {
          .vertical-cards-slider .slick-dots {
            bottom: -35px;
          }
        }
        @media (max-width: 767px) {
          .vertical-cards-slider .slick-dots {
            bottom: -25px;
          }
        }
        .vertical-cards-slider .slick-dots li {
          width: auto;
          height: auto;
          margin: 0;
        }
        .vertical-cards-slider .slick-dots li button {
          width: 16px;
          height: 16px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .vertical-cards-slider .slick-dots li button:before {
          content: "";
          width: 16px;
          height: 16px;
          border: 1px solid #003350;
          border-radius: 50%;
          opacity: 1;
          display: block;
          position: relative;
        }
        .vertical-cards-slider .slick-dots li.slick-active button:before {
          width: 32px;
          height: 16px;
          border-radius: 9999px;
          border: none;
          background: linear-gradient(to right, #8ebce5, #003350);
        }
      `}</style>
    </div>
  );
}
